import { useState, useEffect, useCallback } from 'react';
import { pushAPI } from '../services/api';

// Helper function to convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [permission, setPermission] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const isSupported = useCallback(() => {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }, []);

  useEffect(() => {
    if (!isSupported()) return;

    // Check current permission status
    setPermission(Notification.permission);

    // Check if already subscribed
    const checkSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (err) {
        console.error('Error checking push subscription:', err);
      }
    };
    
    checkSubscription();
  }, [isSupported]);

  const requestPermission = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!isSupported()) {
        throw new Error('Push notifications are not supported in this browser');
      }

      const result = await Notification.requestPermission();
      setPermission(result);

      return result === 'granted';
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  const subscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!isSupported()) {
        throw new Error('Push notifications are not supported');
      }

      if (Notification.permission !== 'granted') {
        throw new Error('Notification permission not granted');
      }

      // Register service worker if not already registered
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key from backend
      const { publicKey } = await pushAPI.getVapidPublicKey();

      if (!publicKey) {
        throw new Error('VAPID public key not configured on server');
      }

      // Convert base64 VAPID key to Uint8Array
      const convertedVapidKey = urlBase64ToUint8Array(publicKey);

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey,
      });

      // Send subscription to backend
      await pushAPI.subscribe(subscription.toJSON(), navigator.userAgent);

      setIsSubscribed(true);
      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      setIsSubscribed(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        throw new Error('No active subscription found');
      }

      // Notify backend of unsubscription
      await pushAPI.unsubscribe(subscription.endpoint);

      // Unsubscribe from push service
      await subscription.unsubscribe();
      setIsSubscribed(false);
      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const testNotification = useCallback(async () => {
    try {
      await pushAPI.testNotification();
      return true;
    } catch (err) {
      console.error('Error sending test notification:', err);
      return false;
    }
  }, []);

  return {
    isSupported: isSupported(),
    permission,
    isSubscribed,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    testNotification,
  };
}
